import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Key, Users, Shield, Check, X, Trash2, Plus, UserPlus,
  Crown, Loader2, Mail, ExternalLink
} from 'lucide-react';
import { AppNav } from '@/components/AppNav';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVIDERS = [
  { id: 'gemini', name: 'Google Gemini', docsUrl: 'https://aistudio.google.com/apikey' },
  { id: 'openai', name: 'OpenAI (GPT)', docsUrl: 'https://platform.openai.com/api-keys' },
  { id: 'anthropic', name: 'Anthropic (Claude)', docsUrl: 'https://console.anthropic.com/settings/keys' }
];

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [myInvites, setMyInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Form states
  const [newKeyProvider, setNewKeyProvider] = useState('gemini');
  const [newApiKey, setNewApiKey] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [keysRes, teamRes, invitesRes] = await Promise.all([
        axios.get(`${API}/settings/api-keys`, { withCredentials: true }),
        axios.get(`${API}/teams`, { withCredentials: true }),
        axios.get(`${API}/teams/invites`, { withCredentials: true })
      ]);
      
      setApiKeys(keysRes.data.api_keys);
      setTeam(teamRes.data.team);
      setMembers(teamRes.data.members);
      setPendingInvites(teamRes.data.pending_invites || []);
      setMyInvites(invitesRes.data.invites || []);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKey = async () => {
    if (!newApiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setSavingKey(true);
    try {
      await axios.post(
        `${API}/settings/api-keys`,
        { provider: newKeyProvider, api_key: newApiKey },
        { withCredentials: true }
      );
      toast.success('API key saved!');
      setNewApiKey('');
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save key');
    } finally {
      setSavingKey(false);
    }
  };

  const handleDeleteKey = async (provider) => {
    if (!window.confirm(`Delete ${provider} API key?`)) return;
    
    try {
      await axios.delete(`${API}/settings/api-keys/${provider}`, { withCredentials: true });
      toast.success('API key deleted');
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error('Failed to delete key');
    }
  };

  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }

    setCreatingTeam(true);
    try {
      await axios.post(`${API}/teams`, { name: newTeamName }, { withCredentials: true });
      toast.success('Team created! You are now the admin.');
      setNewTeamName('');
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }

    setInviting(true);
    try {
      await axios.post(`${API}/teams/invite`, { email: inviteEmail }, { withCredentials: true });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/teams/invites/${inviteId}/accept`, {}, { withCredentials: true });
      toast.success('Joined team!');
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error('Failed to accept invite');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await axios.post(`${API}/teams/invites/${inviteId}/decline`, {}, { withCredentials: true });
      toast.info('Invite declined');
      fetchData();
    } catch (error) {
      toast.error('Failed to decline invite');
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this member from the team?')) return;
    
    try {
      await axios.delete(`${API}/teams/members/${memberId}`, { withCredentials: true });
      toast.success('Member removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#00E599] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb] text-slate-900" data-testid="settings-page">
      <div className="fixed inset-0 hero-glow" />
      
      <div className="relative z-10">
        <AppNav />

        <div className="px-6 py-12 md:px-12">
          <div className="max-w-5xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>Settings</h1>
              <p className="text-slate-400 mb-8">Manage your API keys, team, and preferences</p>

              {/* User Info */}
              <Card className="glass-card border-white/5 mb-8">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    {user?.picture && (
                      <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                    )}
                    <div>
                      <h2 className="text-xl font-semibold">{user?.name}</h2>
                      <p className="text-slate-400">{user?.email}</p>
                      <Badge className={
                        user?.role === 'super_admin' 
                          ? 'bg-[#D946EF]/10 text-[#D946EF] border-[#D946EF]/20 mt-2'
                          : user?.role === 'admin'
                            ? 'bg-[#00E599]/10 text-[#00E599] border-[#00E599]/20 mt-2'
                            : 'bg-slate-800 text-slate-300 border-slate-700 mt-2'
                      }>
                        {user?.role === 'super_admin' && <Crown className="w-3 h-3 mr-1" />}
                        {user?.role?.replace('_', ' ').toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Tabs defaultValue="api-keys" className="w-full">
                <TabsList className="bg-[#0B0F1A] mb-6">
                  <TabsTrigger value="api-keys" className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black">
                    <Key className="w-4 h-4 mr-2" />
                    API Keys
                  </TabsTrigger>
                  <TabsTrigger value="team" className="data-[state=active]:bg-[#00E599] data-[state=active]:text-black">
                    <Users className="w-4 h-4 mr-2" />
                    Team
                  </TabsTrigger>
                </TabsList>

                {/* API Keys Tab */}
                <TabsContent value="api-keys">
                  <div className="grid gap-6">
                    {/* Current Keys */}
                    <Card className="glass-card border-white/5">
                      <CardHeader>
                        <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Your API Keys</CardTitle>
                        <CardDescription>Manage your LLM provider API keys</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {user?.role === 'super_admin' && (
                          <div className="p-4 rounded-lg bg-[#D946EF]/10 border border-[#D946EF]/20">
                            <div className="flex items-center gap-2">
                              <Crown className="w-5 h-5 text-[#D946EF]" />
                              <span className="font-semibold text-[#D946EF]">Super Admin</span>
                            </div>
                            <p className="text-sm text-slate-300 mt-1">
                              You have access to the system's master API key for all AI features.
                            </p>
                          </div>
                        )}
                        
                        {apiKeys.map((key) => (
                          <div key={key.provider} className="flex items-center justify-between p-4 rounded-lg bg-black/30 border border-white/5">
                            <div>
                              <p className="font-medium">{key.display_name}</p>
                              <p className="text-sm text-slate-500">
                                {key.is_configured ? (
                                  <span className="text-green-400 flex items-center gap-1">
                                    <Check className="w-3 h-3" /> Configured
                                  </span>
                                ) : (
                                  <span className="text-slate-500">Not configured</span>
                                )}
                              </p>
                            </div>
                            {key.is_configured && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-400 hover:text-red-300"
                                onClick={() => handleDeleteKey(key.provider)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    {/* Add New Key */}
                    <Card className="glass-card border-white/5">
                      <CardHeader>
                        <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Add API Key</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-3 gap-2">
                          {PROVIDERS.map((p) => (
                            <Button
                              key={p.id}
                              variant={newKeyProvider === p.id ? 'default' : 'outline'}
                              className={newKeyProvider === p.id 
                                ? 'bg-[#00E599] text-black hover:bg-[#00CC88]' 
                                : 'border-white/10 hover:bg-white/5'
                              }
                              onClick={() => setNewKeyProvider(p.id)}
                            >
                              {p.name}
                            </Button>
                          ))}
                        </div>
                        
                        <div>
                          <Label>API Key</Label>
                          <Input
                            type="password"
                            placeholder="Enter your API key"
                            value={newApiKey}
                            onChange={(e) => setNewApiKey(e.target.value)}
                            className="bg-black/40 border-white/10 mt-1"
                          />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <a
                            href={PROVIDERS.find(p => p.id === newKeyProvider)?.docsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-[#00E599] hover:text-[#00CC88] flex items-center gap-1"
                          >
                            Get API key <ExternalLink className="w-3 h-3" />
                          </a>
                          <Button
                            className="bg-[#00E599] text-black hover:bg-[#00CC88]"
                            onClick={handleSaveKey}
                            disabled={savingKey || !newApiKey.trim()}
                          >
                            {savingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                            Save Key
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Security Notice */}
                    <Card className="glass-card border-[#00E599]/20 bg-[#00E599]/5">
                      <CardContent className="p-4 flex items-start gap-3">
                        <Shield className="w-5 h-5 text-[#00E599] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-[#00E599]">Security</p>
                          <p className="text-sm text-slate-300">
                            Your API keys are encrypted with AES-256 and only used for making API calls on your behalf.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                {/* Team Tab */}
                <TabsContent value="team">
                  <div className="grid gap-6">
                    {/* Pending Invites */}
                    {myInvites.length > 0 && (
                      <Card className="glass-card border-[#D946EF]/20 bg-[#D946EF]/5">
                        <CardHeader>
                          <CardTitle className="text-[#D946EF]">Team Invites</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {myInvites.map((invite) => (
                            <div key={invite.invite_id} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                              <div>
                                <p className="font-medium">{invite.team_name}</p>
                                <p className="text-sm text-slate-500">Invited to join</p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-[#00E599] text-black hover:bg-[#00CC88]"
                                  onClick={() => handleAcceptInvite(invite.invite_id)}
                                >
                                  Accept
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-400"
                                  onClick={() => handleDeclineInvite(invite.invite_id)}
                                >
                                  Decline
                                </Button>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {team ? (
                      <>
                        {/* Team Info */}
                        <Card className="glass-card border-white/5">
                          <CardHeader>
                            <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>{team.name}</CardTitle>
                            <CardDescription>{members.length} member(s)</CardDescription>
                          </CardHeader>
                          <CardContent className="space-y-4">
                            {/* Members List */}
                            {members.map((member) => (
                              <div key={member.user_id} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center">
                                    {member.name?.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <p className="font-medium">{member.name}</p>
                                    <p className="text-sm text-slate-500">{member.email}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className={member.role === 'admin' ? 'bg-[#00E599]/10 text-[#00E599]' : 'bg-slate-800 text-slate-300'}>
                                    {member.role}
                                  </Badge>
                                  {user?.role === 'admin' && member.user_id !== user.user_id && member.role !== 'admin' && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-400 hover:text-red-300"
                                      onClick={() => handleRemoveMember(member.user_id)}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </CardContent>
                        </Card>

                        {/* Invite Member (Admin only) */}
                        {(user?.role === 'admin' || user?.role === 'super_admin') && (
                          <Card className="glass-card border-white/5">
                            <CardHeader>
                              <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>
                                <UserPlus className="w-5 h-5 mr-2 inline" />
                                Invite Team Member
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="flex gap-3">
                                <Input
                                  type="email"
                                  placeholder="colleague@company.com"
                                  value={inviteEmail}
                                  onChange={(e) => setInviteEmail(e.target.value)}
                                  className="bg-black/40 border-white/10 flex-1"
                                />
                                <Button
                                  className="bg-[#00E599] text-black hover:bg-[#00CC88]"
                                  onClick={handleInvite}
                                  disabled={inviting}
                                >
                                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                                  Invite
                                </Button>
                              </div>
                              
                              {/* Pending Invites */}
                              {pendingInvites.length > 0 && (
                                <div className="mt-4">
                                  <p className="text-sm text-slate-500 mb-2">Pending Invites</p>
                                  {pendingInvites.map((inv) => (
                                    <div key={inv.invite_id} className="text-sm text-slate-400 py-1">
                                      {inv.email} - pending
                                    </div>
                                  ))}
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        )}
                      </>
                    ) : (
                      /* Create Team */
                      <Card className="glass-card border-white/5">
                        <CardHeader>
                          <CardTitle style={{ fontFamily: 'Unbounded, sans-serif' }}>Create a Team</CardTitle>
                          <CardDescription>
                            Create a team to collaborate with colleagues. You'll become the team admin.
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="flex gap-3">
                            <Input
                              placeholder="Team name"
                              value={newTeamName}
                              onChange={(e) => setNewTeamName(e.target.value)}
                              className="bg-black/40 border-white/10 flex-1"
                            />
                            <Button
                              className="bg-[#00E599] text-black hover:bg-[#00CC88]"
                              onClick={handleCreateTeam}
                              disabled={creatingTeam}
                            >
                              {creatingTeam ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                              Create
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
